import { z } from 'zod/v4';
import type { ModelId } from '@/lib/ai/models';
import type { UserId, SubscriptionId } from '@/lib/types';

export const subscriptionPlanSchema = z.enum(['free', 'plus']);
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>;

export interface PlanLimits {
  aiQuestionsPerMonth: number;
  maxDocuments: number | null;
  allowedModels: ModelId[];
  features: {
    webSearch: boolean;
    thinkingToggle: boolean;
  };
}

export const subscriptionStatusSchema = z.enum([
  'active',
  'trialing',
  'canceled',
  'past_due',
  'unpaid',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const userSubscriptionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  plan: subscriptionPlanSchema,
  status: subscriptionStatusSchema,
  trialEndsAt: z.string().nullable(),
  currentPeriodStart: z.string(),
  currentPeriodEnd: z.string(),
  cancelAtPeriodEnd: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type UserSubscriptionDto = z.infer<typeof userSubscriptionSchema>;

export function toUserSubscription(dto: UserSubscriptionDto): UserSubscription {
  return {
    ...dto,
    id: dto.id as SubscriptionId,
    userId: dto.userId as UserId,
  };
}

export interface UserSubscription {
  id: SubscriptionId;
  userId: UserId;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UsageInfo {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string;
}

export interface QuotaCheckResult extends UsageInfo {
  allowed: boolean;
}

export interface SubscriptionResponse {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string;
  features: {
    webSearch: boolean;
    thinkingToggle: boolean;
  };
  aiQuota: {
    used: number;
    limit: number;
    remaining: number;
    resetsAt: string;
  };
  documentQuota: {
    used: number;
    limit: number | null;
    remaining: number | null;
  };
}

export interface ModelAccessResult {
  allowed: boolean;
  reason?: string;
}

export interface FeatureAccessResult {
  allowed: boolean;
  reason?: string;
}
